import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function EncounterTimer({
  initialSeconds = 0,
  initialRunning = false,
  targetMinutes = 20,
  disabled = false,
  onElapsedChange,
  onStatusChange,
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(initialRunning);
  const mountedRef = React.useRef(false);

  // Initialize ONLY on first mount, NEVER update from props after that
  useEffect(() => {
    if (!mountedRef.current) {
      setElapsedSeconds(initialSeconds);
      setIsRunning(initialRunning);
      mountedRef.current = true;
    }
    // Intentionally no dependencies - only runs once on mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onElapsedChange?.(elapsedSeconds);
  }, [elapsedSeconds, onElapsedChange]);

  useEffect(() => {
    onStatusChange?.(isRunning);
  }, [isRunning, onStatusChange]);

  useEffect(() => {
    if (!isRunning || disabled) return undefined;
    const intervalId = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isRunning, disabled]);

  const targetSeconds = useMemo(() => targetMinutes * 60, [targetMinutes]);
  const progress = useMemo(() => Math.min((elapsedSeconds / targetSeconds) * 100, 100), [elapsedSeconds, targetSeconds]);

  const handleToggle = () => {
    if (disabled) return;
    setIsRunning((prev) => !prev);
  };

  const handleReset = () => {
    if (disabled) return;
    setElapsedSeconds(0);
    setIsRunning(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255,255,255,0.04)",
        borderRadius: 2,
        p: 2,
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Encounter Timer
          </Typography>
          <Typography variant="h4" fontWeight={500}>
            {formatTime(elapsedSeconds)}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Target {targetMinutes} min
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          mb: 2,
          backgroundColor: "rgba(255,255,255,0.1)",
          "& .MuiLinearProgress-bar": {
            background: progress >= 100 ? "linear-gradient(90deg,#d76d87,#79b8c9)" : "linear-gradient(90deg,#79b8c9,#d76d87)",
          },
        }}
      />

      <Box display="flex" alignItems="center" gap={1}>
        <Button
          variant={isRunning ? "outlined" : "contained"}
          color="secondary"
          onClick={handleToggle}
          disabled={disabled}
          startIcon={isRunning ? <PauseIcon /> : <PlayArrowIcon />}
        >
          {isRunning ? "Pause" : "Start"}
        </Button>
        <Tooltip title="Reset timer">
          <span>
            <IconButton onClick={handleReset} disabled={disabled || elapsedSeconds === 0} aria-label="Reset timer">
              <RestartAltIcon aria-label="Reset timer" fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
          {elapsedSeconds > targetSeconds ? `+${formatTime(elapsedSeconds - targetSeconds)}` : "On track"}
        </Typography>
      </Box>
    </Box>
  );
}

EncounterTimer.propTypes = {
  initialSeconds: PropTypes.number,
  initialRunning: PropTypes.bool,
  targetMinutes: PropTypes.number,
  disabled: PropTypes.bool,
  onElapsedChange: PropTypes.func,
  onStatusChange: PropTypes.func,
};

export default EncounterTimer;
